import { NamedView, View, ViewType } from "@common/types/views/views";
import { useCallback, useMemo, useState } from "react";
import { useViews } from "@api/view.api";
import { WORKSPACE_USER_CACHE_KEYS } from "../useWorkspaceUserCache";
import { ORGANIZATION_USER_CACHE_KEYS } from "../useOrganizationUserCache";
import { useScopedCache } from "../useScopedCache";
import { useViewScope } from "./useViewScope";

interface UseViewCollectionOptions<T extends View> {
    viewType: ViewType;
    builtInViews: Array<NamedView<T>>;
    cacheKey?: WORKSPACE_USER_CACHE_KEYS | ORGANIZATION_USER_CACHE_KEYS;
    /**
     * Base view when nothing is cached, and in place of a cached view that no
     * longer exists. Without it, the first built-in view is picked and a
     * missing cached view resolves to none.
     */
    fallbackBaseViewId?: string;
    /**
     * Some built-in views are still missing because their inputs are loading.
     * Holds off on the fallback until they appear.
     */
    isLoadingBuiltInViews?: boolean;
}

export interface UseViewCollectionReturn<T extends View> {
    allViews: Array<NamedView<T>>;
    baseView: NamedView<T> | null;
    /** The picked view's id. Once views load it matches `baseView`, so a removed cached id reads as the fallback. */
    baseViewId: string | null;
    /** Saved or built-in views are still loading. */
    isLoadingViews: boolean;
    setBaseViewId: (viewId: string | null) => void;
}

export function useViewCollection<T extends View>({
    viewType,
    builtInViews,
    cacheKey,
    fallbackBaseViewId,
    isLoadingBuiltInViews = false,
}: UseViewCollectionOptions<T>): UseViewCollectionReturn<T> {
    const viewScope = useViewScope();
    const {
        cachedValue: cachedBaseViewId,
        setCachedValue: setCachedBaseViewId,
    } = useScopedCache<string | null>(cacheKey);

    const [baseViewId, setBaseViewId] = useState<string | null>(() => {
        if (cacheKey && cachedBaseViewId !== undefined) {
            return cachedBaseViewId;
        }
        return fallbackBaseViewId ?? builtInViews[0]?.id ?? null;
    });

    const { data: views, isLoading: isLoadingSavedViews } = useViews({
        scope: viewScope,
        type: viewType,
    });
    const isLoadingViews = isLoadingSavedViews || isLoadingBuiltInViews;

    const allViews = useMemo(
        () => [...builtInViews, ...(views ?? [])] as Array<NamedView<T>>,
        [builtInViews, views],
    );

    const baseView = useMemo(() => {
        if (!baseViewId) {
            return null;
        }
        const selectedView = allViews.find((view) => view.id === baseViewId);
        if (selectedView || isLoadingViews || !fallbackBaseViewId) {
            return selectedView ?? null;
        }
        return allViews.find((view) => view.id === fallbackBaseViewId) ?? null;
    }, [baseViewId, allViews, isLoadingViews, fallbackBaseViewId]);
    const resolvedBaseViewId = isLoadingViews
        ? baseViewId
        : (baseView?.id ?? null);

    const setBaseViewIdWithCache = useCallback(
        (viewId: string | null) => {
            setBaseViewId(viewId);
            setCachedBaseViewId(viewId);
        },
        [setCachedBaseViewId],
    );

    return useMemo(
        () => ({
            allViews,
            baseView,
            baseViewId: resolvedBaseViewId,
            isLoadingViews,
            setBaseViewId: setBaseViewIdWithCache,
        }),
        [
            allViews,
            baseView,
            resolvedBaseViewId,
            isLoadingViews,
            setBaseViewIdWithCache,
        ],
    );
}
